import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
  HiCheckCircle,
  HiExclamation,
  HiXCircle,
  HiInformationCircle,
  HiAdjustments,
  HiSearch,
  HiChevronDown,
  HiChevronRight,
  HiDatabase,
  HiPlus,
  HiTrash,
  HiStar,
  HiOutlineStar,
  HiChevronLeft
} from "react-icons/hi";
import {
  TbSettings,
  TbScale,
  TbNetwork,
  TbDeviceDesktop,
  TbBuildingSkyscraper,
  TbCloud,
  TbFingerprint,
  TbShieldCheck,
  TbShieldExclamation,
  TbHistory
} from "react-icons/tb";
import { Modal, Button, Form } from 'react-bootstrap';
import CategoryIconPicker, { AVAILABLE_ICONS } from './CategoryIconPicker';
import './AdminCategoryThresholds.css';

/* Scan-type grouping config (DEPRECATED - now fallback) */
const DEFAULT_SCAN_GROUPS = [
  { key: 'Network Scan', label: 'Network Scan', icon: 'TbNetwork', color: '#3b82f6', bg: '#eff6ff', border: '#dbeafe' },
  { key: 'Localhost Scan', label: 'Localhost Scan', icon: 'TbDeviceDesktop', color: '#0d9488', bg: '#f0fdfa', border: '#ccfbf1' },
  { key: 'AD Scan', label: 'AD Scan', icon: 'TbBuildingSkyscraper', color: '#8b5cf6', bg: '#f5f3ff', border: '#ede9fe' },
  { key: 'MS365 Scan', label: 'MS365 Scan', icon: 'TbCloud', color: '#f59e0b', bg: '#fffbeb', border: '#fef3c7' },
  { key: 'PII Scan', label: 'TbFingerprint', icon: 'Fingerprint', color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
  { key: 'Infrastructure', label: 'Infrastructure', icon: 'TbSettings', color: '#d946ef', bg: '#fdf4ff', border: '#fae8ff' },
  { key: 'Managed IT/Service Experience', label: 'Managed IT / Service Experience', icon: 'TbSettings', color: '#6366f1', bg: '#eef2ff', border: '#e0e7ff' },
  { key: 'Security', label: 'Security', icon: 'TbShieldCheck', color: '#0ea5e9', bg: '#f0f9ff', border: '#e0f2fe' },
  { key: 'Cyber Security (Threat & Exposure)', label: 'Cyber Security (Threat & Exposure)', icon: 'TbShieldExclamation', color: '#f59e0b', bg: '#fffbeb', border: '#fef3c7' }
];

// Map backend names to frontend icons
const SCAN_GROUP_ICONS = {
  ...AVAILABLE_ICONS,
  'Wifi': TbNetwork,
  'Monitor': TbDeviceDesktop,
  'Users': TbBuildingSkyscraper,
  'Cloud': TbCloud,
  'Fingerprint': TbFingerprint,
  'Settings': TbSettings,
  'ShieldCheck': TbShieldCheck,
  'AlertTriangle': TbShieldExclamation
};

// Robust icon component resolver
const getGroupIcon = (iconName) => {
  if (!iconName) return TbNetwork;

  // 1. Try hardcoded mapper
  if (SCAN_GROUP_ICONS[iconName]) return SCAN_GROUP_ICONS[iconName];

  // 2. Try Available Icons (CategoryIconPicker)
  if (AVAILABLE_ICONS[iconName]) return AVAILABLE_ICONS[iconName];

  // 3. Try react-icons/tb directly if string looks like it
  // (Note: we can't easily dynamic import here, so we rely on the maps)

  return TbNetwork;
};

/* ─── Tiny sub-components ─── */
const InputField = ({ label, value, onChange, type = "number", min, max }) => (
  <div className="d-flex flex-column flex-grow-1">
    <label className="text-secondary fw-bold mb-1" style={{ fontSize: '0.7rem' }}>{label}</label>
    <input type={type} value={value} onChange={onChange} className="form-control form-control-sm" min={min} max={max} style={{ padding: '0.45rem 0.5rem', borderRadius: '8px' }} />
  </div>
);

const OperatorSelect = ({ value, onChange }) => (
  <select value={value} onChange={onChange} className="form-select form-select-sm" style={{ borderRadius: '8px', padding: '0.45rem' }}>
    <option value="<">&lt;</option>
    <option value="<=">&le;</option>
    <option value=">">&gt;</option>
    <option value=">=">&ge;</option>
    <option value="==">==</option>
    <option value="Between">Between</option>
  </select>
);

const ThresholdVarSelector = ({ onInsert }) => (
  <select
    className="form-select form-select-sm"
    style={{ fontSize: '0.65rem', width: 'auto', padding: '0 4px', height: '20px', marginLeft: 'auto', border: '1px solid #d1d5db', cursor: 'pointer', background: '#f9fafb' }}
    value=""
    onChange={(e) => {
      if (e.target.value) {
        onInsert(e.target.value);
        e.target.value = "";
      }
    }}
  >
    <option value="">+ Var</option>
    <option value="{count}">Count</option>
    <option value="{total}">Total</option>
    <option value="{percent}">%</option>
  </select>
);

const MultiNoteField = ({ label, tier, rules, onChange, idPrefix }) => {
  const notes = Array.isArray(rules?.notes) ? rules.notes : (rules?.note ? [{ text: rules.note, isDefault: true }] : [{ text: '', isDefault: true }]);

  const handleUpdateNote = (index, text) => {
    const newNotes = [...notes];
    newNotes[index] = { ...newNotes[index], text };

    // Sync to top-level note if this is the default
    const updateObj = { notes: newNotes };
    if (newNotes[index].isDefault) {
      updateObj.note = text;
    }
    onChange(updateObj);
  };

  const handleAddNote = () => {
    const newNotes = [...notes, { text: '', isDefault: false }];
    onChange({ notes: newNotes });
  };

  const handleRemoveNote = (index) => {
    if (notes.length <= 1) return;
    const newNotes = notes.filter((_, i) => i !== index);
    const wasDefault = notes[index].isDefault;

    // If we removed the default, set the first one as default
    if (wasDefault && newNotes.length > 0) {
      newNotes[0].isDefault = true;
    }

    const updateObj = { notes: newNotes };
    const defaultNote = newNotes.find(n => n.isDefault);
    if (defaultNote) {
      updateObj.note = defaultNote.text;
    }

    onChange(updateObj);
  };

  const handleSetDefault = (index) => {
    const newNotes = notes.map((n, i) => ({
      ...n,
      isDefault: i === index
    }));
    onChange({
      notes: newNotes,
      note: newNotes[index].text
    });
  };

  return (
    <div className="mt-2 d-flex flex-column">
      <div className="d-flex align-items-center mb-1">
        <label className="text-secondary fw-bold" style={{ fontSize: '0.65rem' }}>{label}</label>
        <button
          type="button"
          onClick={handleAddNote}
          className="btn btn-link btn-sm p-0 ms-2 text-primary fw-bold"
          style={{ fontSize: '0.65rem', textDecoration: 'none' }}
        >
          + Add Template
        </button>
      </div>

      <div className="d-flex flex-column gap-2">
        {notes.map((note, idx) => (
          <div key={idx} className="position-relative">
            <div className="d-flex align-items-center mb-1">
              <span className="text-muted" style={{ fontSize: '0.6rem' }}>Template #{idx + 1}</span>
              <div className="ms-auto d-flex align-items-center gap-2">
                <button
                  type="button"
                  title={note.isDefault ? "Current Default" : "Set as Default"}
                  onClick={() => handleSetDefault(idx)}
                  className={`btn btn-sm p-0 ${note.isDefault ? 'text-warning' : 'text-secondary opacity-50'}`}
                >
                  {note.isDefault ? <HiStar size={14} /> : <HiOutlineStar size={14} />}
                </button>
                {notes.length > 1 && (
                  <button
                    type="button"
                    title="Remove Template"
                    onClick={() => handleRemoveNote(idx)}
                    className="btn btn-sm p-0 text-danger opacity-50 hover-opacity-100"
                  >
                    <HiTrash size={14} />
                  </button>
                )}
                <ThresholdVarSelector onInsert={(v) => {
                  const el = document.getElementById(`${idPrefix}-${idx}`);
                  const currentVal = note.text || '';
                  if (el) {
                    const start = el.selectionStart;
                    const end = el.selectionEnd;
                    const newVal = currentVal.substring(0, start) + v + currentVal.substring(end);
                    handleUpdateNote(idx, newVal);
                    setTimeout(() => {
                      el.focus();
                      el.setSelectionRange(start + v.length, start + v.length);
                    }, 0);
                  } else {
                    handleUpdateNote(idx, currentVal + v);
                  }
                }} />
              </div>
            </div>
            <textarea
              id={`${idPrefix}-${idx}`}
              className={`form-control form-control-sm ${note.isDefault ? 'border-primary bg-primary bg-opacity-5' : ''}`}
              rows={2}
              value={note.text || ''}
              onChange={e => handleUpdateNote(idx, e.target.value)}
              placeholder="Enter note template..."
              style={{ fontSize: '0.72rem', padding: '0.4rem', borderRadius: '6px', lineHeight: '1.4' }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── Main component ─── */
const AdminCategoryThresholds = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editState, setEditState] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [toast, setToast] = useState(null);
  const [scanGroups, setScanGroups] = useState([]);
  const [expandedSections, setExpandedSections] = useState({});
  const [activeSubTab, setActiveSubTab] = useState('categories'); // 'categories' or 'groups'
  const [expandedGroups, setExpandedGroups] = useState({});

  const toggleGroupExpansion = (groupKey) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };


  // Group Management State
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null); // When not null, we are editing this group
  const [groupForm, setGroupForm] = useState({ key: '', label: '', icon: 'Settings', color: '#3b82f6' });

  // Create Modal State
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    scan_type: 'Network Scan',
    data_sources: [{ scan_type: 'Network Scan', path: '' }],
    metric: '',
    value_type: 'numeric',
    icon: '',
    Good: { operator: '>=', value: 90 },
    Moderate: { operator: 'Between', lower: 70, upper: 89 },
    Bad: { operator: '<', value: 70 }
  });
  const [explorerTargetIndex, setExplorerTargetIndex] = useState(null);

  // JSON Explorer State
  const [samples, setSamples] = useState([]);
  const [selectedSample, setSelectedSample] = useState(null);
  const [explorerPath, setExplorerPath] = useState([]);
  const [currentJsonNode, setCurrentJsonNode] = useState(null);
  const [showExplorer, setShowExplorer] = useState(false);
  const [explorerStep, setExplorerStep] = useState('drill'); // 'drill' or 'finalize'

  // Add Existing Category State
  const [showExistingModal, setShowExistingModal] = useState(false);
  const [browseTargetGroup, setBrowseTargetGroup] = useState(null);
  const [browseSearch, setBrowseSearch] = useState('');




  /* Helper to merge keys from all items in an array for a representative schema */

  const mergeJsonNodes = useCallback((nodes) => {
    if (!Array.isArray(nodes)) return nodes;
    if (nodes.length === 0) return null;

    const merged = {};
    nodes.forEach(node => {
      if (node && typeof node === 'object' && !Array.isArray(node)) {
        Object.keys(node).forEach(key => {
          if (merged[key] === undefined) {
            merged[key] = node[key];
          } else if (typeof merged[key] === 'object' && typeof node[key] === 'object' && !Array.isArray(merged[key]) && !Array.isArray(node[key])) {
            // Recurse for nested objects (but only if both are objects)
            merged[key] = mergeJsonNodes([merged[key], node[key]]);
          }
        });
      }
    });
    return merged;
  }, []);

  useEffect(() => {
    if (showExplorer && explorerTargetIndex !== null) {
      const rowType = categoryForm.data_sources[explorerTargetIndex]?.scan_type;
      const rootKey = getRootKeyForScanType(rowType);
      
      const latestSample = samples.find(s => s.id === 'encrypted-9-12') || samples[0];
      if (latestSample && latestSample.data) {
        setSelectedSample(latestSample);
        
        let rawData = latestSample.data;
        if (rawData.data && typeof rawData.data === 'object' && !Array.isArray(rawData.data)) {
          rawData = rawData.data;
        }

        let node = rawData;
        let initialPath = [];

        if (rootKey && rawData[rootKey] !== undefined) {
          node = rawData[rootKey];
          if (Array.isArray(node) && node.length > 0) {
            initialPath = [`${rootKey}[*]`];
            node = mergeJsonNodes(node);
          } else {
            initialPath = [rootKey];
          }
        }

        setExplorerPath(initialPath);
        setCurrentJsonNode(node);
        setExplorerStep('drill');
      }
    }
  }, [categoryForm.data_sources, showExplorer, explorerTargetIndex, samples, mergeJsonNodes]);

  /* Show toast notification */
  const showToast = (message, isError = false) => {
    setToast({ message, isError });
    setTimeout(() => setToast(null), 3000);
  };

  /* ── Data fetching ── */
  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('Admintoken');
      const res = await axios.get('/s1/admin/thresholds/categories', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setCategories(res.data.data);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      showToast('Failed to load categories', true);
    }
    setLoading(false);
  }, []);

  const fetchSamples = useCallback(async () => {
    try {
      const token = localStorage.getItem('Admintoken');
      const res = await axios.get('/s1/admin/thresholds/samples', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setSamples(res.data.data);
      }
    } catch (err) {
      console.error('Error fetching samples:', err);
    }
  }, []);

  const fetchScanGroups = useCallback(async () => {
    try {
      const token = localStorage.getItem('Admintoken');
      const res = await axios.get('/s1/admin/thresholds/scan-groups', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setScanGroups(res.data.data);
        // Initialize expanded sections for new groups
        setExpandedSections(prev => {
          const next = { ...prev };
          res.data.data.forEach(g => {
            if (next[g.key] === undefined) next[g.key] = true;
          });
          return next;
        });
      }
    } catch (err) {
      console.error('Error fetching scan groups:', err);
    }
  }, []);

  useEffect(() => {
    fetchScanGroups();
    fetchCategories();
    fetchSamples();
  }, [fetchScanGroups, fetchCategories, fetchSamples]);

  /* ── Edit handlers ── */
  const handleEdit = (cat) => {
    setEditingId(cat.id);
    setEditState({
      thresholds: JSON.parse(JSON.stringify(cat.default_thresholds || {})),
      icon: cat.icon || '',
      scan_type: cat.scan_type || ''
    });
  };

  const handleCancel = () => { setEditingId(null); setEditState(null); };

  const handleSave = async (catId, overrides = {}) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('Admintoken');
      await axios.put(`/s1/admin/thresholds/categories/${catId}`, {
        default_thresholds: overrides.thresholds || editState?.thresholds,
        icon: overrides.icon !== undefined ? overrides.icon : (editState?.icon || ''),
        scan_type: overrides.scan_type !== undefined ? overrides.scan_type : (editState?.scan_type || categories.find(c => c.id === catId)?.scan_type)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchCategories();
      setEditingId(null);
      setEditState(null);
      showToast('Category updated successfully');
    } catch (err) {
      console.error('Failed to save default threshold', err);
      showToast('Failed to save changes', true);
    }
    setSaving(false);
  };

  const handleDelete = async (catId) => {
    if (!window.confirm('Are you sure you want to delete this category threshold? This will remove all tenant overrides associated with it.')) {
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('Admintoken');
      await axios.delete(`/s1/admin/thresholds/categories/${catId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchCategories();
      showToast('Category deleted successfully');
    } catch (err) {
      console.error('Failed to delete category', err);
      showToast('Failed to delete category', true);
    }
    setSaving(false);
  };

  const updateRule = (tier, key, val) => {
    setEditState(prev => {
      const isObjectUpdate = typeof val === 'object' && val !== null;
      if (key === null && isObjectUpdate) {
        return {
          ...prev,
          thresholds: {
            ...prev.thresholds,
            [tier]: { ...prev.thresholds[tier], ...val }
          }
        };
      }
      return {
        ...prev,
        thresholds: {
          ...prev.thresholds,
          [tier]: { ...prev.thresholds[tier], [key]: val }
        }
      };
    });
  };

  const updateIcon = (iconName) => {
    setEditState(prev => ({
      ...prev,
      icon: iconName
    }));
  };

  const addDataSource = () => {
    setCategoryForm(prev => ({
      ...prev,
      data_sources: [...prev.data_sources, { scan_type: prev.scan_type === 'Infrastructure' ? 'Network Scan' : prev.scan_type, path: '' }]
    }));
  };

  const removeDataSource = (index) => {
    setCategoryForm(prev => ({
      ...prev,
      data_sources: prev.data_sources.filter((_, i) => i !== index)
    }));
  };

  const updateDataSource = (index, field, value) => {
    setCategoryForm(prev => {
      const newSources = [...prev.data_sources];
      newSources[index] = { ...newSources[index], [field]: value };
      return { ...prev, data_sources: newSources };
    });
  };

  const updateCreateRule = (tier, key, val) => {
    setCategoryForm(prev => {
      const isObjectUpdate = typeof val === 'object' && val !== null;
      return {
        ...prev,
        [tier]: isObjectUpdate
          ? { ...prev[tier], ...val }
          : { ...prev[tier], [key]: val }
      };
    });
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('Admintoken');

      // Join multiple sources with their types for storage
      const finalDataSource = categoryForm.data_sources
        .filter(s => s.path.trim() !== '')
        .map(s => `[${s.scan_type}] ${s.path}`)
        .join(' + ');

      const payload = {
        name: categoryForm.name,
        description: categoryForm.description,
        value_type: categoryForm.value_type,
        scan_type: categoryForm.scan_type,
        data_source: finalDataSource,
        metric: categoryForm.metric,
        icon: categoryForm.icon,
        default_thresholds: {
          Good: categoryForm.Good,
          Moderate: categoryForm.Moderate,
          Bad: categoryForm.Bad
        }
      };
      await axios.post('/s1/admin/thresholds/categories', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchCategories();
      setShowCategoryModal(false);
      showToast('Category created successfully');
      setCategoryForm({
        name: '', description: '', scan_type: 'Network Scan', data_sources: [{ scan_type: 'Network Scan', path: '' }], metric: '', value_type: 'numeric', icon: '',
        Good: { operator: '>=', value: 90 }, Moderate: { operator: 'Between', lower: 70, upper: 89 }, Bad: { operator: '<', value: 70 }
      });
    } catch (err) {
      console.error('Failed to create category', err);
      if (err.response?.data?.error) {
        showToast(err.response.data.error, true);
      } else {
        showToast('Failed to create category', true);
      }
    }
    setSaving(false);
  };

  const handleCloneCategory = async (cat) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('Admintoken');
      const isSameGroup = cat.scan_type === browseTargetGroup.key;
      const payload = {
        name: isSameGroup ? `${cat.name} (Copy)` : cat.name,
        description: cat.description,
        value_type: cat.value_type,
        scan_type: browseTargetGroup.key,
        data_source: cat.data_source,
        metric: cat.metric,
        icon: cat.icon,
        default_thresholds: cat.default_thresholds
      };
      await axios.post('/s1/admin/thresholds/categories', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchCategories();
      showToast(`Category "${payload.name}" added successfully`);
      setShowExistingModal(false);
    } catch (err) {
      console.error('Failed to clone category', err);
      showToast('Failed to add category', true);
    }
    setSaving(false);
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('Admintoken');
      // Generate light bg and border from color
      const payload = {
        ...groupForm,
        bg: `${groupForm.color}10`, // 10% opacity hex
        border: `${groupForm.color}30` // 30% opacity hex
      };

      if (editingGroup) {
        await axios.put(`/s1/admin/thresholds/scan-groups/${editingGroup.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showToast('Group updated successfully');
      } else {
        await axios.post('/s1/admin/thresholds/scan-groups', payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showToast('Group created successfully');
      }

      await fetchScanGroups();
      setShowGroupModal(false);
      setEditingGroup(null);
      setGroupForm({ key: '', label: '', icon: 'Settings', color: '#3b82f6' });
    } catch (err) {
      console.error('Failed to manage group', err);
      showToast(err.response?.data?.error || 'Failed to process group', true);
    }
    setSaving(false);
  };

  const handleCloseGroupModal = () => {
    if (saving) return;
    setShowGroupModal(false);
    setEditingGroup(null);
    setGroupForm({ key: '', label: '', icon: 'Settings', color: '#3b82f6' });
  };

  const handleUnassign = async (catId) => {
    if (!window.confirm("Are you sure you want to unassign this threshold from this group? It will be moved to 'Dashboard Grid Categories'.")) return;
    try {
      const token = localStorage.getItem('Admintoken');
      await axios.put(`/s1/admin/thresholds/categories/${catId}/move`, {
        scan_type: null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCategories();
      showToast('Threshold unassigned successfully');
    } catch (err) {
      console.error('Failed to unassign category', err);
      showToast('Failed to unassign threshold.', true);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this group? All associated categories will be moved to Dashboard Grid Categories.')) {
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('Admintoken');
      await axios.delete(`/s1/admin/thresholds/scan-groups/${groupId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchScanGroups();
      await fetchCategories(); // Refresh categories to show reassignment
      showToast('Group deleted successfully');
    } catch (err) {
      console.error('Failed to delete group', err);
      showToast(err.response?.data?.error || 'Failed to delete group', true);
    }
    setSaving(false);
  };

  /* ── Explorer Helpers ── */
  const getRootKeyForScanType = (type) => {
    switch (type) {
      case 'Network Scan': return 'network_devices';
      case 'Localhost Scan': return 'localhost';
      case 'AD Scan': return 'Ad Scan';
      case 'MS365 Scan': return 'MS365';
      case 'PII Scan': return 'PII';
      default: return null;
    }
  };

  const handleOpenExplorer = (index) => {
    setExplorerTargetIndex(index);
    setShowExplorer(true);
  };

  const handleExploreDrill = (key, value) => {
    if (Array.isArray(value)) {
      setCurrentJsonNode(mergeJsonNodes(value));
      setExplorerPath([...explorerPath, `${key}[*]`]);
    } else {
      setCurrentJsonNode(value);
      setExplorerPath([...explorerPath, key]);
    }
  };

  const handleExploreBack = () => {
    const newPath = explorerPath.slice(0, -1);
    setExplorerPath(newPath);
    let node = selectedSample.data;
    newPath.forEach(p => {
      if (node) {
        if (p.endsWith('[*]')) {
          const actualKey = p.replace('[*]', '');
          const rawValue = node[actualKey];
          node = Array.isArray(rawValue) ? mergeJsonNodes(rawValue) : rawValue;
        } else {
          node = node[p];
        }
      }
    });
    setCurrentJsonNode(node);
  };

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  /* ── Grouped + filtered data ── */
  const groupedCategories = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    const activeGroups = scanGroups.length > 0 ? scanGroups : DEFAULT_SCAN_GROUPS;

    return activeGroups.map(group => {
      const items = categories.filter(cat => {
        if (cat.scan_type !== group.key) return false;
        if (!q) return true;
        return (
          (cat.name || '').toLowerCase().includes(q) ||
          (cat.description || '').toLowerCase().includes(q) ||
          (cat.metric || '').toLowerCase().includes(q) ||
          (cat.data_source || '').toLowerCase().includes(q)
        );
      });
      return { ...group, items };
    });
  }, [categories, searchQuery, scanGroups]);

  /* Uncategorized items (dashboard grid categories, etc.) */
  const uncategorizedItems = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    const activeGroups = scanGroups.length > 0 ? scanGroups : DEFAULT_SCAN_GROUPS;

    return categories.filter(cat => {
      // If categories belong to one of our active groups, they aren't uncategorized
      if (activeGroups.some(g => g.key === cat.scan_type)) return false;
      if (!q) return true;
      return (cat.name || '').toLowerCase().includes(q) || (cat.description || '').toLowerCase().includes(q);
    });
  }, [categories, searchQuery, scanGroups]);

  const totalMatchCount = useMemo(() => {
    return groupedCategories.reduce((sum, g) => sum + g.items.length, 0) + uncategorizedItems.length;
  }, [groupedCategories, uncategorizedItems]);

  /* ── Loading state ── */
  if (loading) return (
    <div className="p-4 d-flex align-items-center">
      <div className="spinner-border text-primary me-2"></div> Loading default thresholds...
    </div>
  );

  /* ────────────────────────────────────────────
     RENDER
     ──────────────────────────────────────────── */
  return (
    <div className="act-container">
      {/* Toast */}
      {toast && (
        <div className={`act-toast ${toast.isError ? 'error' : ''}`}>
          {toast.message}
        </div>
      )}

      {/* ─ Page Header ─ */}
      <div className="act-header">
        <div className="d-flex align-items-center justify-content-between mb-2 w-100">
          <div className="ms-auto d-flex align-items-center gap-3">
            {activeSubTab === 'categories' && (
              <div className="d-flex gap-2">
                <button
                  className="btn btn-outline-primary d-flex align-items-center gap-2"
                  onClick={() => {
                    setBrowseTargetGroup(null);
                    setShowExistingModal(true);
                  }}
                >
                  <HiPlus size={18} />
                  Add Existing
                </button>
                <button
                  className="btn btn-primary d-flex align-items-center gap-2"
                  onClick={() => setShowCategoryModal(true)}
                >
                  <HiPlus size={18} />
                  Add
                </button>
              </div>
            )}
            {activeSubTab === 'groups' && (
              <button
                className="btn btn-primary d-flex align-items-center gap-2"
                onClick={() => {
                  setEditingGroup(null);
                  setGroupForm({ key: '', label: '', icon: 'Settings', color: '#3b82f6' });
                  setShowGroupModal(true);
                }}
              >
                <HiPlus size={18} />
                Create Group
              </button>
            )}
          </div>
        </div>

        {/* ─ Sub-Tab Navigation ─ */}
        <div className="act-sub-nav">
          <button
            className={`act-sub-nav-item ${activeSubTab === 'categories' ? 'active' : ''}`}
            onClick={() => setActiveSubTab('categories')}
          >
            Categories
          </button>
          <button
            className={`act-sub-nav-item ${activeSubTab === 'groups' ? 'active' : ''}`}
            onClick={() => setActiveSubTab('groups')}
          >
            Groups
          </button>
        </div>

        {/* ─ Search (Only for categories) ─ */}
        {activeSubTab === 'categories' && (
          <div className="act-search-wrapper mt-3">
            <HiSearch className="act-search-icon" size={18} />
            <input
              type="text"
              className="act-search-input"
              placeholder="Search categories or metrics…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <span className="act-search-count">{totalMatchCount} result{totalMatchCount !== 1 ? 's' : ''}</span>
            )}
          </div>
        )}
      </div>

      {/* ─ Scan-Type Sections ─ */}
      <div className="p-3 p-md-4" style={{ background: 'var(--bg-primary, #f8fafc)' }}>
        {activeSubTab === 'categories' ? (
          <>
        {groupedCategories.map(group => {
          if (group.items.length === 0 && searchQuery) return null;
          const isExpanded = expandedSections[group.key];

          return (
            <div key={group.key} className="act-scan-section mb-3">
              {/* Section Header */}
              <div className="act-scan-header-wrapper d-flex align-items-center w-100">
                <button
                  className="act-scan-header flex-grow-1"
                  onClick={() => toggleSection(group.key)}
                >
                  <div className="d-flex align-items-center">
                    <div className="act-scan-icon-box" style={{ background: group.bg, color: group.color }}>
                      {(() => {
                        const IconComp = getGroupIcon(group.icon);
                        return <IconComp size={22} />;
                      })()}
                    </div>
                    <span className="act-scan-label">{group.label}</span>
                    <span className="act-scan-count" style={{ background: group.bg, color: group.color, border: `1px solid ${group.border}` }}>
                      {group.items.length}
                    </span>
                  </div>
                  {isExpanded ? <HiChevronDown size={20} className="text-secondary" /> : <HiChevronRight size={20} className="text-secondary" />}
                </button>
                    <div className="d-flex align-items-center gap-2 pe-3">
                      <button
                        className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1 fw-bold px-2 py-1 me-1"
                        style={{ fontSize: '0.7rem', borderRadius: '6px' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setBrowseTargetGroup(group);
                          setShowExistingModal(true);
                        }}
                      >
                        <HiPlus size={14} />
                        Add Existing
                      </button>
                      <button
                        className="btn btn-sm btn-link text-secondary p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingGroup(group);
                      setGroupForm({ key: group.key, label: group.label, icon: group.icon, color: group.color });
                      setShowGroupModal(true);
                    }}
                    title="Edit Group Configuration"
                  >
                    <TbSettings size={18} />
                  </button>
                  <button
                    className="btn btn-sm btn-link text-danger p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteGroup(group.id);
                    }}
                    title="Delete Group"
                  >
                    <HiTrash size={18} />
                  </button>
                </div>
              </div>

              {/* Section Content */}
              {isExpanded && (
                <div className="act-scan-body">
                  {group.items.length === 0 ? (
                    <div className="text-center text-secondary py-4" style={{ fontSize: '0.85rem' }}>
                      No categories loaded for this scan type.
                    </div>
                  ) : (
                    group.items.map(cat => (
                      <DefaultCategoryCard
                        key={cat.id}
                        cat={cat}
                        scanGroups={scanGroups}
                        scanColor={group.color}
                        isEditing={editingId === cat.id}
                        editState={editState}
                        setEditState={setEditState}
                        saving={saving}
                        onEdit={handleEdit}
                        onCancel={handleCancel}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        onUpdateRule={updateRule}
                        onUpdateIcon={updateIcon}
                      />
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Uncategorized (dashboard grid categories, etc.) */}
        {uncategorizedItems.length > 0 && (
          <div className="act-scan-section mb-3">
            <div className="act-scan-header-wrapper d-flex align-items-center w-100">
              <div className="act-scan-header flex-grow-1" style={{ cursor: 'default' }}>
                <div className="d-flex align-items-center">
                  <div className="act-scan-icon-box" style={{ background: '#f1f5f9', color: '#64748b' }}>
                    <TbScale size={22} />
                  </div>
                  <span className="act-scan-label">Dashboard Grid Categories</span>
                  <span className="act-scan-count" style={{ background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' }}>
                    {uncategorizedItems.length}
                  </span>
                </div>
              </div>
            </div>
            <div className="act-scan-body">
               {uncategorizedItems.map(cat => (
                  <DefaultCategoryCard
                    key={cat.id}
                    cat={cat}
                    scanGroups={scanGroups}
                    scanColor="#64748b"
                    isEditing={editingId === cat.id}
                    editState={editState}
                    setEditState={setEditState}
                    saving={saving}
                    onEdit={handleEdit}
                    onCancel={handleCancel}
                    onSave={handleSave}
                    onDelete={handleDelete}
                    onUpdateRule={updateRule}
                    onUpdateIcon={updateIcon}
                  />
                ))}
            </div>
          </div>
        )}
          </>
        ) : (
          <div className="row g-4">
            {scanGroups.map(group => (
              <div key={group.id} className="col-12 col-lg-6">
                <div className="card border-0 shadow-sm rounded-4 overflow-hidden h-100">
                  <div className="card-header bg-white py-3 px-4 d-flex align-items-center justify-content-between border-bottom-light">
                    <div className="d-flex align-items-center gap-3">
                      <div className="p-2 rounded-3 shadow-sm" style={{ background: group.bg, color: group.color, border: `1px solid ${group.border}` }}>
                        {(() => {
                          const IconComp = getGroupIcon(group.icon);
                          return <IconComp size={24} />;
                        })()}
                      </div>
                      <div>
                        <h5 className="mb-0 fw-bold">{group.label}</h5>
                        <div className="text-muted small">Key: <code>{group.key}</code></div>
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-outline-primary btn-sm rounded-pill px-3 d-flex align-items-center gap-1"
                        onClick={() => {
                          setBrowseTargetGroup(group);
                          setShowExistingModal(true);
                        }}
                      >
                        <HiPlus size={14} />
                        Add
                      </button>
                      <button
                        className="btn btn-outline-primary btn-sm rounded-pill px-3"
                        onClick={() => {
                          setEditingGroup(group);
                          setGroupForm({ key: group.key, label: group.label, icon: group.icon, color: group.color });
                          setShowGroupModal(true);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-outline-danger btn-sm rounded-pill px-3"
                        onClick={() => handleDeleteGroup(group.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  <div className="card-body p-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <span className="text-secondary small fw-bold uppercase">Assigned Thresholds</span>
                      <span className="badge bg-light text-primary border">{categories.filter(c => c.scan_type === group.key).length} Items</span>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      {categories
                        .filter(c => c.scan_type === group.key)
                        .slice(0, expandedGroups[group.key] ? undefined : 8)
                        .map(c => (
                          <span key={c.id} className="badge bg-white text-dark border fw-normal py-2 px-3 rounded-pill shadow-xs animate-in fade-in slide-in-from-bottom-1 duration-200 group-badge-premium">
                            {c.name}
                            <HiXCircle 
                              size={14} 
                              className="group-badge-remove-icon" 
                              title="Unassign Threshold"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUnassign(c.id);
                              }}
                            />
                          </span>
                        ))}
                      {categories.filter(c => c.scan_type === group.key).length > 8 && (
                        <button 
                          className="btn btn-link btn-sm p-0 text-primary text-decoration-none small ps-2 fw-bold"
                          onClick={() => toggleGroupExpansion(group.key)}
                          style={{ transition: 'all 0.2s ease' }}
                        >
                          {expandedGroups[group.key] ? 'show less' : `+${categories.filter(c => c.scan_type === group.key).length - 8} more`}
                        </button>
                      )}
                      {categories.filter(c => c.scan_type === group.key).length === 0 && (
                        <div className="text-muted small italic py-2">No categories assigned to this group yet.</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            <div className="col-12 col-lg-6">
              <div 
                className="card border-dashed border-2 rounded-4 h-100 d-flex align-items-center justify-content-center p-5 text-center transition-all hover-bg-light"
                style={{ cursor: 'pointer', borderColor: '#e2e8f0' }}
                onClick={() => {
                  setEditingGroup(null);
                  setGroupForm({ key: '', label: '', icon: 'Settings', color: '#3b82f6' });
                  setShowGroupModal(true);
                }}
              >
                <div>
                  <div className="bg-primary bg-opacity-10 p-3 rounded-circle d-inline-flex mb-3">
                    <HiPlus size={32} className="text-primary" />
                  </div>
                  <h5 className="fw-bold">Create New Group</h5>
                  <p className="text-secondary small mb-0">Define a new collection to organize global metric thresholds.</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="act-footer">
        <HiInformationCircle className="me-2" size={14} />
        Changes to default thresholds cascade to all tenants without custom overrides.
      </div>

      {/* Create Category Modal */}
      <Modal show={showCategoryModal} onHide={() => !saving && setShowCategoryModal(false)} size="xl" centered className="tm-premium-modal">
        <Modal.Header closeButton={!saving} className="tm-modal-header-premium">
          <Modal.Title className="tm-modal-title fw-bold d-flex align-items-center">
            <div className="bg-success bg-opacity-10 p-2 rounded-3 me-3">
              <HiPlus className="text-success" size={24} />
            </div>
            Create New Category Threshold
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0 bg-white">
          <Form onSubmit={handleCreateCategory} className="p-4">
            {/* Section 1: Basic Configuration */}
            <div className="tm-modal-section">
              <div className="tm-modal-section-title">
                <TbShieldCheck size={16} /> Basic Configuration
              </div>
              <div className="row g-4">
                <div className="col-12 col-md-6">
                  <Form.Group className="tm-field-group">
                    <Form.Label className="tm-form-label">Category Name *</Form.Label>
                    <Form.Control
                      required
                      type="text"
                      className="tm-form-control-premium"
                      placeholder="e.g. Memory Latency Spike"
                      value={categoryForm.name}
                      onChange={e => setCategoryForm({ ...categoryForm, name: e.target.value })}
                    />
                  </Form.Group>
                </div>
                <div className="col-12 col-md-3">
                  <Form.Group className="tm-field-group">
                    <Form.Label className="tm-form-label">Administrative Group</Form.Label>
                    <Form.Select
                      className="tm-form-control-premium"
                      value={categoryForm.scan_type}
                      onChange={e => setCategoryForm({ ...categoryForm, scan_type: e.target.value })}
                    >
                      {['Network Scan', 'Localhost Scan', 'AD Scan', 'MS365 Scan', 'PII Scan'].map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-12 col-md-3">
                  <Form.Group className="tm-field-group">
                    <Form.Label className="tm-form-label">Metric Unit</Form.Label>
                    <Form.Control
                      type="text"
                      className="tm-form-control-premium"
                      placeholder="e.g. % or IOPS"
                      value={categoryForm.metric}
                      onChange={e => setCategoryForm({ ...categoryForm, metric: e.target.value })}
                    />
                  </Form.Group>
                </div>
                <div className="col-12 col-md-8">
                  <Form.Group className="tm-field-group">
                    <Form.Label className="tm-form-label">Administrative Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      className="tm-form-control-premium"
                      placeholder="Define what this metric evaluates..."
                      value={categoryForm.description}
                      onChange={e => setCategoryForm({ ...categoryForm, description: e.target.value })}
                    />
                  </Form.Group>
                </div>
                <div className="col-12 col-md-4">
                  <CategoryIconPicker
                    label="Metric Visual Icon"
                    value={categoryForm.icon}
                    onChange={(icon) => setCategoryForm({ ...categoryForm, icon })}
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Data Source & Intelligence */}
            <div className="tm-modal-section">
              <div className="d-flex align-items-center justify-content-between mb-4">
                <div className="tm-modal-section-title mb-0">
                  <HiDatabase size={16} /> Data Selection Strategy
                </div>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="rounded-pill px-3 fw-bold border-2"
                  onClick={addDataSource}
                >
                  <HiPlus size={14} className="me-1" />
                  Chain Dataset
                </Button>
              </div>

              <div className="mb-4">
                {categoryForm.data_sources.map((source, index) => (
                  <div key={index} className="mb-3 transition-all">
                    <div className="input-group">
                      <Form.Select
                        className="tm-form-control-premium border-end-0"
                        style={{ maxWidth: '200px', borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                        value={source.scan_type}
                        onChange={e => updateDataSource(index, 'scan_type', e.target.value)}
                      >
                        {['Network Scan', 'Localhost Scan', 'AD Scan', 'MS365 Scan', 'PII Scan'].map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </Form.Select>
                      <Form.Control
                        type="text"
                        className="tm-form-control-premium border-start-0 border-end-0"
                        style={{ borderRadius: 0 }}
                        placeholder="Path to discovered field"
                        value={source.path}
                        onChange={e => updateDataSource(index, 'path', e.target.value)}
                      />
                      <Button
                        variant="primary"
                        style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                        onClick={() => {
                          setExplorerTargetIndex(index);
                          handleOpenExplorer(index);
                        }}
                        className="px-4 fw-bold shadow-sm"
                      >
                        <HiSearch size={16} className="me-2" />
                        Explore
                      </Button>
                      {categoryForm.data_sources.length > 1 && (
                        <Button
                          variant="danger"
                          className="ms-2 rounded-circle p-2 d-flex align-items-center justify-content-center shadow-sm"
                          style={{ width: '38px', height: '38px' }}
                          onClick={() => removeDataSource(index)}
                        >
                          <HiXCircle size={18} />
                        </Button>
                      )}
                    </div>
                    {/* Inline JSON Explorer per Data Source */}
                    {showExplorer && explorerTargetIndex === index && (
                      <div className="tm-explorer-panel-premium shadow-lg animate-in fade-in zoom-in duration-200 mt-3" style={{ maxWidth: '100%' }}>
                        <div className="tm-explorer-panel-header">
                          <div className="d-flex align-items-center gap-2 text-white">
                            <TbFingerprint className="text-info" size={20} />
                            <span className="fw-bold small">Live Schema Discovery: {source.scan_type}</span>
                          </div>
                          <Button variant="link" size="sm" className="text-secondary p-0 text-decoration-none" onClick={() => setShowExplorer(false)}>Close Utility</Button>
                        </div>

                        <div className="tm-explorer-path-bar">
                          <span className="opacity-50">root</span>
                          {explorerPath.map((p, idx) => (
                            <React.Fragment key={idx}>
                              <HiChevronRight size={10} className="opacity-30" />
                              <span className="tm-explorer-path-chip">{p}</span>
                            </React.Fragment>
                          ))}
                        </div>

                        {selectedSample && (
                          <div className="tm-explorer-scroll-area">
                            {explorerPath.length > (getRootKeyForScanType(source.scan_type) ? 1 : 0) && (
                              <button
                                type="button"
                                className="btn btn-link text-info text-decoration-none d-flex align-items-center small mb-2 p-0"
                                onClick={handleExploreBack}
                              >
                                <HiChevronLeft size={14} className="me-1" />
                                Up Directory
                              </button>
                            )}

                            {currentJsonNode && typeof currentJsonNode === 'object' ? (
                              Object.keys(currentJsonNode).map(k => {
                                const value = currentJsonNode[k];
                                const isDrillable = value && typeof value === 'object';

                                return (
                                  <div key={k} className="tm-explorer-item-premium">
                                    <div className="d-flex align-items-center gap-2 overflow-hidden">
                                      <span className="tm-explorer-key">{k}</span>
                                      {!isDrillable && (
                                        <span className="tm-explorer-value-preview truncate">: {JSON.stringify(value)}</span>
                                      )}
                                    </div>
                                    <div className="d-flex gap-2">
                                      {isDrillable ? (
                                        <Button size="xs" variant="outline-info" className="fw-bold border-2" onClick={() => handleExploreDrill(k, value)}>Enter</Button>
                                      ) : (
                                        <Button size="xs" variant="info" className="fw-bold text-white shadow-sm" onClick={() => {
                                          const finalPath = [...explorerPath, k].join('.');
                                          updateDataSource(explorerTargetIndex, 'path', finalPath);
                                          setShowExplorer(false);
                                        }}>Select</Button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="text-center text-muted py-5 small opacity-50">Discovery depth achieved. Select a leaf node.</div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Section 3: Threshold Criteria */}
            <div className="tm-modal-section mb-0">
              <div className="tm-modal-section-title">
                <HiAdjustments size={16} /> Scoring Logic Thresholds
              </div>

              <div className="row g-4">
                <div className="col-12 col-md-4">
                  <div className="p-3 rounded-4 border-2" style={{ background: 'rgba(16, 185, 129, 0.05)', border: '2px solid rgba(16, 185, 129, 0.1)' }}>
                    <div className="d-flex align-items-center gap-2 text-success fw-bold mb-3 small uppercase tracking-wider">
                      <HiCheckCircle size={18} /> Healthy
                    </div>
                    <OperatorSelect value={categoryForm.Good.operator} onChange={e => updateCreateRule('Good', 'operator', e.target.value)} />
                    <div className="mt-2">
                      {categoryForm.Good.operator === 'Between' ? (
                        <div className="d-flex gap-2">
                          <InputField label="Min" value={categoryForm.Good.lower} onChange={e => updateCreateRule('Good', 'lower', parseFloat(e.target.value) || 0)} />
                          <InputField label="Max" value={categoryForm.Good.upper} onChange={e => updateCreateRule('Good', 'upper', parseFloat(e.target.value) || 0)} />
                        </div>
                      ) : (
                        <InputField label="Target" value={categoryForm.Good.value} onChange={e => updateCreateRule('Good', 'value', parseFloat(e.target.value) || 0)} />
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <div className="p-3 rounded-4 border-2" style={{ background: 'rgba(245, 158, 11, 0.05)', border: '2px solid rgba(245, 158, 11, 0.1)' }}>
                    <div className="d-flex align-items-center gap-2 text-warning fw-bold mb-3 small uppercase tracking-wider">
                      <HiExclamation size={18} /> Moderate
                    </div>
                    <OperatorSelect value={categoryForm.Moderate.operator} onChange={e => updateCreateRule('Moderate', 'operator', e.target.value)} />
                    <div className="mt-2">
                      {categoryForm.Moderate.operator === 'Between' ? (
                        <div className="d-flex gap-2">
                          <InputField label="Min" value={categoryForm.Moderate.lower} onChange={e => updateCreateRule('Moderate', 'lower', parseFloat(e.target.value) || 0)} />
                          <InputField label="Max" value={categoryForm.Moderate.upper} onChange={e => updateCreateRule('Moderate', 'upper', parseFloat(e.target.value) || 0)} />
                        </div>
                      ) : (
                        <InputField label="Target" value={categoryForm.Moderate.value} onChange={e => updateCreateRule('Moderate', 'value', parseFloat(e.target.value) || 0)} />
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <div className="p-3 rounded-4 border-2" style={{ background: 'rgba(239, 68, 68, 0.05)', border: '2px solid rgba(239, 68, 68, 0.1)' }}>
                    <div className="d-flex align-items-center gap-2 text-danger fw-bold mb-3 small uppercase tracking-wider">
                      <HiXCircle size={18} /> Critical
                    </div>
                    <OperatorSelect value={categoryForm.Bad.operator} onChange={e => updateCreateRule('Bad', 'operator', e.target.value)} />
                    <div className="mt-2">
                      {categoryForm.Bad.operator === 'Between' ? (
                        <div className="d-flex gap-2">
                          <InputField label="Min" value={categoryForm.Bad.lower} onChange={e => updateCreateRule('Bad', 'lower', parseFloat(e.target.value) || 0)} />
                          <InputField label="Max" value={categoryForm.Bad.upper} onChange={e => updateCreateRule('Bad', 'upper', parseFloat(e.target.value) || 0)} />
                        </div>
                      ) : (
                        <InputField label="Target" value={categoryForm.Bad.value} onChange={e => updateCreateRule('Bad', 'value', parseFloat(e.target.value) || 0)} />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="tm-modal-footer-premium mx-n4 mb-n4 mt-4">
              <Button variant="outline-secondary" className="px-4 rounded-pill fw-bold border-2" onClick={() => setShowCategoryModal(false)} disabled={saving}>
                Discard
              </Button>
              <Button variant="primary" type="submit" className="px-5 rounded-pill fw-bold shadow-lg border-0" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }} disabled={saving}>
                {saving ? 'Creating Threshold...' : 'Create Threshold'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Group Management Modal */}
      <Modal show={showGroupModal} onHide={handleCloseGroupModal} size="lg" centered className="tm-premium-modal" enforceFocus={false}>
        <Modal.Header closeButton={!saving} className="tm-modal-header-premium">
          <Modal.Title className="tm-modal-title fw-bold d-flex align-items-center">
            <div className="bg-primary bg-opacity-10 p-2 rounded-3 me-3">
              <TbSettings className="text-primary" size={24} />
            </div>
            Manage Scan Groups
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <div className="p-4 bg-white">
            {/* List of Existing Groups */}
            <div className="tm-modal-section mb-4">
              <div className="tm-modal-section-title">
                <HiDatabase size={16} /> Existing Groups
              </div>
              <div className="list-group list-group-flush border rounded-3 overflow-hidden shadow-sm">
                {scanGroups.map(group => (
                  <div key={group.id} className="list-group-item d-flex align-items-center justify-content-between py-3 px-3 border-light">
                    <div className="d-flex align-items-center gap-3">
                      <div className="p-2 rounded-lg shadow-sm" style={{ background: group.bg, color: group.color, border: `1px solid ${group.border}` }}>
                        {(() => {
                          const IconComp = getGroupIcon(group.icon);
                          return <IconComp size={20} />;
                        })()}
                      </div>
                      <div>
                        <div className="fw-bold text-dark">{group.label}</div>
                        <div className="text-muted small">Key: <code className="text-secondary">{group.key}</code></div>
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <Button
                        variant="light"
                        size="sm"
                        className="rounded-circle p-2 border-0 shadow-sm"
                        onClick={() => {
                          setEditingGroup(group);
                          setGroupForm({ key: group.key, label: group.label, icon: group.icon, color: group.color });
                        }}
                      >
                        <TbSettings size={16} className="text-primary" />
                      </Button>
                      <Button
                        variant="light"
                        size="sm"
                        className="rounded-circle p-2 border-0 shadow-sm"
                        onClick={() => handleDeleteGroup(group.id)}
                      >
                        <HiTrash size={16} className="text-danger" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="tm-modal-section">
              <div className="tm-modal-section-title">
                <HiPlus size={16} /> {editingGroup ? `Edit: ${editingGroup.label}` : 'Create New Group'}
              </div>
              <Form onSubmit={handleCreateGroup}>
                <div className="row g-4">
                  <div className="col-12">
                    <Form.Group className="tm-field-group">
                      <Form.Label className="tm-form-label">Display Label *</Form.Label>
                      <Form.Control
                        required
                        type="text"
                        className="tm-form-control-premium"
                        placeholder="e.g. Server Core Metrics"
                        value={groupForm.label}
                        onChange={e => {
                          const val = e.target.value;
                          setGroupForm(prev => ({
                            ...prev,
                            label: val,
                            key: editingGroup ? prev.key : val
                          }));
                        }}
                      />
                    </Form.Group>
                  </div>

                  <div className="col-12 col-md-6">
                    <CategoryIconPicker
                      label="Dashboard Icon"
                      value={groupForm.icon}
                      onChange={(icon) => setGroupForm({ ...groupForm, icon })}
                    />
                  </div>

                  <div className="col-12 col-md-6">
                    <Form.Group className="tm-field-group">
                      <Form.Label className="tm-form-label">Accent Theme Color</Form.Label>
                      <Form.Control
                        type="color"
                        value={groupForm.color}
                        onChange={e => setGroupForm({ ...groupForm, color: e.target.value })}
                        className="tm-form-control-premium w-100 p-1"
                        style={{ height: '42px' }}
                      />
                    </Form.Group>
                  </div>
                </div>

                <div className="d-flex justify-content-end mt-4 pt-3 gap-3 border-top border-light">
                  <Button variant="outline-secondary" className="px-4 rounded-pill fw-bold" onClick={handleCloseGroupModal} disabled={saving}>
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit" className="px-5 rounded-pill fw-bold shadow border-0" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }} disabled={saving}>
                    {saving ? 'Processing...' : (editingGroup ? 'Apply Updates' : 'Initialize Group')}
                  </Button>
                </div>
              </Form>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Add Existing Category Modal */}
      <Modal show={showExistingModal} onHide={() => setShowExistingModal(false)} size="lg" centered className="tm-premium-modal">
        <Modal.Header closeButton className="tm-modal-header-premium">
          <Modal.Title className="tm-modal-title fw-bold d-flex align-items-center">
            <div className="bg-primary bg-opacity-10 p-2 rounded-3 me-3">
              <HiPlus className="text-primary" size={24} />
            </div>
            {browseTargetGroup ? `Add Existing Category to ${browseTargetGroup.label}` : "Add Existing Category"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-light p-0">
          <div className="p-3 border-bottom bg-white">
            <div className="position-relative">
              <HiSearch className="position-absolute translate-middle-y text-muted" style={{ left: '12px', top: '50%' }} />
              <input
                type="text"
                className="form-control ps-5 border-0 bg-light"
                placeholder="Search across all categories..."
                value={browseSearch}
                onChange={e => setBrowseSearch(e.target.value)}
                style={{ borderRadius: '10px' }}
              />
            </div>
            
            {!browseTargetGroup && (
              <div className="mt-3">
                <label className="text-secondary small fw-bold mb-2 d-block">Select Target Group</label>
                <div className="d-flex flex-wrap gap-2">
                  {scanGroups.map(g => (
                    <button
                      key={g.key}
                      onClick={() => setBrowseTargetGroup(g)}
                      className={`btn btn-sm rounded-pill px-3 py-1 border transition-all ${browseTargetGroup?.key === g.key ? 'btn-primary border-primary' : 'btn-outline-secondary bg-white'}`}
                      style={{ fontSize: '0.75rem' }}
                    >
                      {g.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="overflow-auto" style={{ maxHeight: '450px' }}>
            <div className="p-3">
              {categories
                .filter(c => {
                  const q = browseSearch.toLowerCase().trim();
                  if (!q) return true;
                  return (c.name || '').toLowerCase().includes(q) || (c.description || '').toLowerCase().includes(q) || (c.scan_type || '').toLowerCase().includes(q);
                })
                .map(cat => (
                  <div key={cat.id} className="card border-0 shadow-sm mb-2 rounded-3 hover-shadow transition-all">
                    <div className="card-body p-3 d-flex align-items-center">
                      <div className="me-3 p-2 bg-light rounded-3 text-secondary">
                        {(() => {
                          const group = scanGroups.find(g => g.key === cat.scan_type);
                          const IconComp = getGroupIcon(cat.icon || group?.icon);
                          return <IconComp size={20} />;
                        })()}
                      </div>
                      <div className="flex-grow-1">
                        <div className="fw-bold text-dark">{cat.name}</div>
                        <div className="text-muted small d-flex align-items-center gap-2">
                          <span className="badge bg-light text-secondary border fw-normal">{cat.scan_type}</span>
                          {cat.metric && <span>&bull; {cat.metric}</span>}
                        </div>
                      </div>
                      <button
                        className="btn btn-primary btn-sm rounded-pill px-3 shadow-sm"
                        disabled={saving || !browseTargetGroup}
                        onClick={() => handleCloneCategory(cat)}
                      >
                        {saving ? <div className="spinner-border spinner-border-sm"></div> : "Add Threshold"}
                      </button>
                    </div>
                  </div>
                ))}
              
              {categories.filter(c => {
                  const q = browseSearch.toLowerCase().trim();
                  if (!q) return true;
                  return (c.name || '').toLowerCase().includes(q) || (c.description || '').toLowerCase().includes(q) || (c.scan_type || '').toLowerCase().includes(q);
              }).length === 0 && (
                <div className="text-center py-5 text-muted">
                  <HiSearch size={48} className="opacity-20 mb-3" />
                  <p>No categories found matching your search.</p>
                </div>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="bg-white border-top">
          <Button variant="link" className="text-secondary text-decoration-none" onClick={() => setShowExistingModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

/* ──────────────────────────────────────────────
   DefaultCategoryCard — renders one category
   with its default threshold tiers (Good/Moderate/Bad)
   ────────────────────────────────────────────── */
const DefaultCategoryCard = ({ cat, scanGroups, scanColor, isEditing, editState, setEditState, saving, onEdit, onCancel, onSave, onDelete, onUpdateRule, onUpdateIcon }) => {
  const currentRules = isEditing ? editState.thresholds : cat.default_thresholds;
  const currentIconName = isEditing ? editState.icon : cat.icon;
  const SelectedIcon = currentIconName && AVAILABLE_ICONS[currentIconName] ? AVAILABLE_ICONS[currentIconName] : null;

  return (
    <div className={`act-category-card ${isEditing ? 'editing' : ''}`}>
      {/* Category Header */}
      <div className="act-cat-header">
        <div className="d-flex align-items-start flex-grow-1">
          <div className="act-cat-color-bar" style={{ background: scanColor }}></div>
          <div className="flex-grow-1">
            <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
              <h3 className="m-0 fw-bold act-cat-name" style={{ fontSize: '0.95rem' }}>
                {SelectedIcon && <SelectedIcon size={18} className="me-2 text-primary" />}
                {cat.name}
              </h3>
              <span className="act-badge-default">
                Global Default
              </span>
            </div>
            <p className="act-cat-description m-0 mb-1" style={{ fontSize: '0.78rem', lineHeight: 1.4 }}>{cat.description}</p>

            {/* Data source + metric info */}
            {(cat.data_source || cat.metric) && (
              <div className="d-flex flex-wrap gap-2 mt-1">
                {cat.data_source && cat.data_source.split(' + ').map((src, idx) => (
                  <span key={idx} style={{
                    display: 'inline-flex', alignItems: 'center',
                    background: '#f0fdf4', color: '#16a34a',
                    border: '1px solid #bbf7d0',
                    fontSize: '0.65rem', fontWeight: 600,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    padding: '2px 8px', borderRadius: '6px',
                    wordBreak: 'break-all'
                  }}>
                    <HiDatabase size={11} className="me-1 opacity-50 flex-shrink-0" />
                    {src}
                  </span>
                ))}

                {/* Dynamic 'Includes' Badge for Infrastructure Scans */}
                {cat.scan_type === 'Infrastructure' && cat.data_source && (() => {
                  // Extract unique scan types from [Type] path format
                  const matches = [...cat.data_source.matchAll(/\[(.*?)\]/g)];
                  const types = [...new Set(matches.map(m => m[1]))];
                  if (types.length === 0) return null;

                  return (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center',
                      background: '#f5f3ff', color: '#7c3aed',
                      border: '1px solid #ddd6fe',
                      fontSize: '0.65rem', fontWeight: 600,
                      padding: '2px 8px', borderRadius: '6px'
                    }}>
                      <TbNetwork size={11} className="me-1 opacity-50" />
                      Includes: {types.join(' & ')}
                    </span>
                  );
                })()}

                {cat.metric && (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center',
                    background: '#fefce8', color: '#a16207',
                    border: '1px solid #fde68a',
                    fontSize: '0.65rem', fontWeight: 600,
                    padding: '1px 8px', borderRadius: '6px'
                  }}>
                    📊 {cat.metric}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="d-flex align-items-center gap-2 flex-shrink-0 ms-3">
          {!isEditing && (
            <>
              <button onClick={() => onDelete(cat.id)} className="btn btn-outline-danger btn-sm d-flex align-items-center">
                <span>Delete</span>
              </button>
              <button onClick={() => onEdit(cat)} className="act-btn-configure d-flex align-items-center">
                <TbSettings size={16} className="me-1" />
                <span>Configure</span>
              </button>
            </>
          )}
        </div>
      </div>

      {isEditing && (
        <div className="px-3 pt-3">
          <div className="row">
            <div className="col-12 col-md-4">
              <CategoryIconPicker
                label="Category Icon"
                value={editState.icon}
                onChange={onUpdateIcon}
              />
            </div>
            <div className="col-12 col-md-8">
              <Form.Label className="fw-bold small mb-1">Reassign Scan Group</Form.Label>
              <Form.Select
                value={editState.scan_type}
                onChange={e => {
                  setEditState(prev => ({ ...prev, scan_type: e.target.value }));
                }}
                className="form-select-sm"
              >
                {scanGroups.map(g => (
                  <option key={g.key} value={g.key}>{g.label}</option>
                ))}
                <option value="">(None / Uncategorized)</option>
              </Form.Select>
            </div>
          </div>
        </div>
      )}

      {/* Tiers */}
      <div className="p-3 row g-3">
        {/* GOOD */}
        <div className="col-12 col-md-4">
          <div className={`act-tier-card act-tier-card-good ${isEditing ? 'border-success' : ''}`}>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="d-flex align-items-center">
                <HiCheckCircle className="text-success me-1" size={16} />
                <span className="text-success fw-bolder text-uppercase" style={{ fontSize: '0.6rem', letterSpacing: '0.05em' }}>Healthy</span>
              </div>
            </div>
            {isEditing && currentRules?.Good ? (
              <div className="d-flex flex-column gap-1">
                <OperatorSelect value={currentRules.Good.operator} onChange={e => onUpdateRule('Good', 'operator', e.target.value)} />
                {currentRules.Good.operator === 'Between' ? (
                  <div className="d-flex align-items-end gap-2">
                    <InputField label="Lower" value={currentRules.Good.lower || ''} onChange={e => onUpdateRule('Good', 'lower', parseFloat(e.target.value) || 0)} />
                    <span className="pb-2 text-secondary fw-bold" style={{ fontSize: '0.75rem' }}>~</span>
                    <InputField label="Upper" value={currentRules.Good.upper || ''} onChange={e => onUpdateRule('Good', 'upper', parseFloat(e.target.value) || 0)} />
                  </div>
                ) : (
                  <InputField label="Threshold" value={currentRules.Good.value || 0} onChange={e => onUpdateRule('Good', 'value', parseFloat(e.target.value) || 0)} />
                )}
                <MultiNoteField
                  label="Healthy Note Templates"
                  idPrefix={`note-default-${cat.id}-Good`}
                  rules={currentRules.Good}
                  onChange={updateObj => onUpdateRule('Good', null, updateObj)}
                />
              </div>
            ) : (
              <div className="d-flex flex-column">
                <span className="act-value-display">{currentRules?.Good ? (currentRules.Good.operator === 'Between' ? `${currentRules.Good.lower} ↔ ${currentRules.Good.upper}` : `${currentRules.Good.operator} ${currentRules.Good.value}`) : 'N/A'}</span>
                <span className="act-label-small">Acceptable Range</span>
                {currentRules?.Good?.note && (
                  <div className="mt-2 p-2 rounded bg-success bg-opacity-10 border border-success border-opacity-10" style={{ fontSize: '0.68rem', color: '#15803d', fontStyle: 'italic', lineHeight: '1.3' }}>
                    {currentRules.Good.note}
                  </div>
                )}
              </div>
            )}
            <HiCheckCircle className="act-tier-icon-bg text-success" size={60} />
          </div>
        </div>

        {/* MODERATE */}
        <div className="col-12 col-md-4">
          <div className={`act-tier-card act-tier-card-moderate ${isEditing ? 'border-warning' : ''}`}>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="d-flex align-items-center">
                <HiExclamation className="text-warning me-1" size={16} />
                <span className="text-warning fw-bolder text-uppercase" style={{ fontSize: '0.6rem', letterSpacing: '0.05em' }}>Warning</span>
              </div>
            </div>
            {isEditing && currentRules?.Moderate ? (
              <div className="d-flex flex-column gap-1">
                <OperatorSelect value={currentRules.Moderate.operator} onChange={e => onUpdateRule('Moderate', 'operator', e.target.value)} />
                {currentRules.Moderate.operator === 'Between' ? (
                  <div className="d-flex align-items-end gap-2">
                    <InputField label="Lower" value={currentRules.Moderate.lower || ''} onChange={e => onUpdateRule('Moderate', 'lower', parseFloat(e.target.value) || 0)} />
                    <span className="pb-2 text-secondary fw-bold" style={{ fontSize: '0.75rem' }}>~</span>
                    <InputField label="Upper" value={currentRules.Moderate.upper || ''} onChange={e => onUpdateRule('Moderate', 'upper', parseFloat(e.target.value) || 0)} />
                  </div>
                ) : (
                  <InputField label="Value" value={currentRules.Moderate.value || 0} onChange={e => onUpdateRule('Moderate', 'value', parseFloat(e.target.value) || 0)} />
                )}
                <MultiNoteField
                  label="Warning Note Templates"
                  idPrefix={`note-default-${cat.id}-Moderate`}
                  rules={currentRules.Moderate}
                  onChange={updateObj => onUpdateRule('Moderate', null, updateObj)}
                />
              </div>
            ) : (
              <div className="d-flex flex-column">
                <span className="act-value-display">{currentRules?.Moderate ? (currentRules.Moderate.operator === 'Between' ? `${currentRules.Moderate.lower} ↔ ${currentRules.Moderate.upper}` : `${currentRules.Moderate.operator} ${currentRules.Moderate.value}`) : 'N/A'}</span>
                <span className="act-label-small">Observation Range</span>
                {currentRules?.Moderate?.note && (
                  <div className="mt-2 p-2 rounded bg-warning bg-opacity-10 border border-warning border-opacity-10" style={{ fontSize: '0.68rem', color: '#a16207', fontStyle: 'italic', lineHeight: '1.3' }}>
                    {currentRules.Moderate.note}
                  </div>
                )}
              </div>
            )}
            <HiExclamation className="act-tier-icon-bg text-warning" size={60} />
          </div>
        </div>

        {/* BAD */}
        <div className="col-12 col-md-4">
          <div className={`act-tier-card act-tier-card-bad ${isEditing ? 'border-danger' : ''}`}>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="d-flex align-items-center">
                <HiXCircle className="text-danger me-1" size={16} />
                <span className="text-danger fw-bolder text-uppercase" style={{ fontSize: '0.6rem', letterSpacing: '0.05em' }}>Critical</span>
              </div>
            </div>
            {isEditing && currentRules?.Bad ? (
              <div className="d-flex flex-column gap-1">
                <OperatorSelect value={currentRules.Bad.operator} onChange={e => onUpdateRule('Bad', 'operator', e.target.value)} />
                {currentRules.Bad.operator === 'Between' ? (
                  <div className="d-flex align-items-end gap-2">
                    <InputField label="Lower" value={currentRules.Bad.lower || ''} onChange={e => onUpdateRule('Bad', 'lower', parseFloat(e.target.value) || 0)} />
                    <span className="pb-2 text-secondary fw-bold" style={{ fontSize: '0.75rem' }}>~</span>
                    <InputField label="Upper" value={currentRules.Bad.upper || ''} onChange={e => onUpdateRule('Bad', 'upper', parseFloat(e.target.value) || 0)} />
                  </div>
                ) : (
                  <InputField label="Threshold" value={currentRules.Bad.value || 0} onChange={e => onUpdateRule('Bad', 'value', parseFloat(e.target.value) || 0)} />
                )}
                <MultiNoteField
                  label="Critical Note Templates"
                  idPrefix={`note-default-${cat.id}-Bad`}
                  rules={currentRules.Bad}
                  onChange={updateObj => onUpdateRule('Bad', null, updateObj)}
                />
              </div>
            ) : (
              <div className="d-flex flex-column">
                <span className="act-value-display">{currentRules?.Bad ? (currentRules.Bad.operator === 'Between' ? `${currentRules.Bad.lower} ↔ ${currentRules.Bad.upper}` : `${currentRules.Bad.operator} ${currentRules.Bad.value}`) : 'N/A'}</span>
                <span className="act-label-small">Incident Threshold</span>
                {currentRules?.Bad?.note && (
                  <div className="mt-2 p-2 rounded bg-danger bg-opacity-10 border border-danger border-opacity-10" style={{ fontSize: '0.68rem', color: '#b91c1c', fontStyle: 'italic', lineHeight: '1.3' }}>
                    {currentRules.Bad.note}
                  </div>
                )}
              </div>
            )}
            <HiXCircle className="act-tier-icon-bg text-danger" size={60} />
          </div>
        </div>
      </div>

      {/* Edit Actions */}
      {isEditing && (
        <div className="px-3 py-2 bg-light border-top d-flex justify-content-end align-items-center">
          <button onClick={onCancel} className="btn btn-outline-secondary btn-sm fw-bold me-2 rounded-3 px-3" style={{ fontSize: '0.8rem' }}>
            Cancel
          </button>
          <button onClick={() => onSave(cat.id)} disabled={saving} className="btn btn-primary btn-sm fw-bold rounded-3 px-3 shadow-sm border-0" style={{ background: '#2563eb', fontSize: '0.8rem' }}>
            {saving ? 'Saving...' : 'Save Defaults'}
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminCategoryThresholds;
